const opened = new Set<string>();

/** Before pushing a page: its Back can pop instead of replacing. */
export const markOpenedInApp = (path: string) => void opened.add(path);

// Next's pages router gives each pushed history entry a new `key` and keeps
// it on replace and reload-free navigation, so a key that differs from the
// one the app loaded on means there is an in-app entry behind this one.
const historyKey = (): unknown => {
  const state: unknown =
    typeof window === "undefined" ? undefined : window.history?.state;
  return state && typeof state === "object" && "key" in state
    ? state.key
    : undefined;
};

let landingKey: unknown;
let landingKnown = false;

/** Call once the router is up; the first call pins the entry the app loaded on. */
export const rememberLandingEntry = () => {
  if (!landingKnown) {
    landingKey = historyKey();
    landingKnown = true;
  }
};

/** Clears a mark set by `markOpenedInApp`, saying whether there was one. */
export const takeOpenedInApp = (path: string) => opened.delete(path);

/** False after a reload or a shared link: nothing in-app to pop back to. */
export const wasOpenedInApp = (path: string) =>
  opened.has(path) || (landingKnown && historyKey() !== landingKey);

// A section is what native shows as a sheet (Settings, People): its X leaves
// every section page at once, back to the page that opened it.
// 0 means the section began at the page the app loaded on: nothing to return to.
const sectionDepth = new Map<unknown, number>();
let lastKey: unknown;
let anyEntry = false;

/** Call on every web page, saying whether it belongs to a section. */
export const rememberSectionEntry = (inSection: boolean) => {
  const key = historyKey();
  if (!inSection) {
    sectionDepth.delete(key);
  } else if (!sectionDepth.has(key)) {
    const before = sectionDepth.get(lastKey);
    sectionDepth.set(
      key,
      !anyEntry || before === 0 ? 0 : before === undefined ? 1 : before + 1,
    );
  }
  lastKey = key;
  anyEntry = true;
};

/** History steps back to the page that opened the section, if it was in-app. */
export const sectionExitSteps = (): number | undefined =>
  sectionDepth.get(historyKey()) || undefined;
